import React, {memo} from 'react';
import cn from 'classnames';
import {useSelector} from 'react-redux';
import {LocationPoint, LocationSuperiorsList} from '@/common/interfaces/location';
import {LocationWithWeather} from '@/app/interfaces/weather';
import {RouteName} from '@/app/constants/routing';
import {useLocalizer} from '@/common/services/localizer';
import {useUrlBuilder} from '@/app/routing/useUrlBuilder';
import {temperatureDisplayReduce} from '@/common/helpers/unitReducers/temperatureDisplayReduce';
import {useToggleState} from '@/common/helpers/hooks/useToggleState';
import {selectTemperatureUnit} from '@/app/store/config/selectors';
import {RouterLink} from '@/common/components/RouterLink';
import {ConditionSmallIcon} from '@/common/components/ConditionSmallIcon';
import {TextLink} from '@/common/components/TextLink';
import styles from './WeatherLinking.css';

interface WeatherLinkingProps {
	locationSuperior: LocationSuperiorsList;
	regionTopLocations: LocationWithWeather[];
	countryTopLocations: LocationPoint[];
}

export const WeatherLinking = memo<WeatherLinkingProps>(props => {
	const {locationSuperior, regionTopLocations, countryTopLocations} = props;

	const [primaryExpanded, togglePrimaryExpanded] = useToggleState(false);
	const [secondaryExpanded, toggleSecondaryExpanded] = useToggleState(false);

	const temperatureUnit = useSelector(selectTemperatureUnit);

	const urlBuilder = useUrlBuilder();

	const t = useLocalizer();

	if (!regionTopLocations.length && !countryTopLocations.length) {
		return null;
	}

	const nearestSuperiorLocation = locationSuperior.region || locationSuperior.district || locationSuperior.country!;

	const mapLinkElement = (
		<RouterLink
			component={TextLink}
			className={styles.mapLink}
			url={urlBuilder(RouteName.map, {location: nearestSuperiorLocation.id})}>
			{t('weatherOnMap')}
		</RouterLink>
	);

	return (
		<div className={styles.root}>
			{!!regionTopLocations.length && (
				<>
					<div className={styles.header}>
						<h2
							className={cn(styles.title, primaryExpanded && styles.isExpanded)}
							role='button'
							onClick={togglePrimaryExpanded}>
							{t('weather')}{' '}
							{[locationSuperior.region?.titleLc, locationSuperior.country?.title]
								.filter(Boolean)
								.join(', ')}
						</h2>
						{mapLinkElement}
					</div>
					<ul
						className={cn(styles.mainList, !primaryExpanded && styles.isHidden)}
						onFocus={primaryExpanded ? undefined : togglePrimaryExpanded}>
						{regionTopLocations.map(({location, weather}) => (
							<li key={location.id} className={styles.mainItem}>
								<div className={styles.mainEntry}>
									<RouterLink
										className={styles.mainLink}
										component={TextLink}
										url={urlBuilder(RouteName.weather, {location})}>
										<span className={styles.mainLinkText}>
											{t('weather')} {location.titleLc}
										</span>
									</RouterLink>

									{weather && (
										<>
											<p className={styles.temperature}>
												{temperatureDisplayReduce(weather.temperature, temperatureUnit)}
											</p>
											<ConditionSmallIcon
												className={styles.conditionIcon}
												condition={weather.condition}
												isNight={weather.isNight}
											/>
										</>
									)}
								</div>
							</li>
						))}
					</ul>
				</>
			)}

			<div className={styles.header}>
				<h2
					className={cn(styles.title, secondaryExpanded && styles.isExpanded)}
					role='button'
					onClick={toggleSecondaryExpanded}>
					{t('showOtherLocations')}
				</h2>
				{!regionTopLocations.length && mapLinkElement}
			</div>
			{!!countryTopLocations.length && (
				<ul
					className={cn(styles.secondaryList, !secondaryExpanded && styles.isHidden)}
					onFocus={secondaryExpanded ? undefined : toggleSecondaryExpanded}>
					{countryTopLocations.map(location => (
						<li key={location.id} className={styles.secondaryItem}>
							<RouterLink
								component={TextLink}
								url={urlBuilder(RouteName.weather, {location})}>
								{t('weather')} {location.title}
							</RouterLink>
						</li>
					))}
				</ul>
			)}
		</div>
	);
});

WeatherLinking.displayName = 'WeatherLinking';
